import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { OnboardingState, OnboardingStepKey, PageKey } from '../types';

const DISMISS_KEY = 'lexcrm.onboarding.dismissed';

/**
 * Dashboard checklist of next steps. Hidden once dismissed (localStorage)
 * or when all steps are complete. Each item has a CTA that navigates to
 * the right page (or opens an action) — page navigation is delegated to
 * the AppShell via onNavigate.
 */
export function OnboardingChecklist({
  onNavigate,
  onConnectWa,
  onConnectGoogle,
}: {
  onNavigate: (page: PageKey) => void;
  onConnectWa?: () => void;
  onConnectGoogle?: () => void;
}) {
  const { t } = useTranslation(['onboarding', 'common']);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    return typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1';
  });

  useEffect(() => {
    let alive = true;
    apiFetch<OnboardingState>('/api/onboarding')
      .then((s) => alive && setState(s))
      .catch(() => alive && setState(null));
    return () => {
      alive = false;
    };
  }, []);

  const counts = useMemo(() => {
    if (!state) return { done: 0, total: 0 };
    return {
      done: state.steps.filter((s) => s.completed).length,
      total: state.steps.length,
    };
  }, [state]);

  if (!state) return null;
  if (state.allDone) return null;
  if (dismissed) return null;

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  return (
    <div className="onboarding-card">
      <div className="onboarding-header">
        <div>
          <div className="onboarding-title">{t('title')}</div>
          <div className="onboarding-sub">
            {t('subtitle')} · {t('progress', counts)}
          </div>
        </div>
        <button
          type="button"
          className="onboarding-dismiss"
          onClick={handleDismiss}
          aria-label={t('dismiss')}
          title={t('dismiss')}
        >
          ×
        </button>
      </div>
      <div className="onboarding-steps">
        {state.steps.map((step) => {
          const action = ctaFor(step.key);
          const isClickable = !step.completed && !!action;
          const handleRowClick = () => {
            if (isClickable) action!({ onNavigate, onConnectWa, onConnectGoogle });
          };
          return (
            <div
              key={step.key}
              className={`onboarding-step ${step.completed ? 'done' : ''} ${isClickable ? 'clickable' : ''}`}
              onClick={handleRowClick}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick();
                      }
                    }
                  : undefined
              }
              style={isClickable ? { cursor: 'pointer' } : undefined}
            >
              <div className={`onboarding-check ${step.completed ? 'done' : ''}`}>
                {step.completed ? '✓' : ''}
              </div>
              <div className="onboarding-step-info">
                <div className="onboarding-step-label">{t(`steps.${step.key}.label`)}</div>
                <div className="onboarding-step-hint">{t(`steps.${step.key}.hint`)}</div>
              </div>
              {isClickable && (
                <span
                  className="btn-link"
                  // The button is now decorative — the whole row handles
                  // the click. Keep the visual cue but stop double-firing.
                  onClick={(e) => e.stopPropagation()}
                  style={{ pointerEvents: 'none' }}
                >
                  {t(`steps.${step.key}.cta`)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type ActionCtx = {
  onNavigate: (p: PageKey) => void;
  onConnectWa?: (() => void) | undefined;
  onConnectGoogle?: (() => void) | undefined;
};

function ctaFor(key: OnboardingStepKey): ((ctx: ActionCtx) => void) | null {
  switch (key) {
    case 'firm':
      return null; // Already done by virtue of being here
    case 'first_client':
      return ({ onNavigate }) => onNavigate('addclient');
    case 'first_case':
      // No standalone "create case" page — direct to clients so user can
      // open a client and use the + תיק חדש button there.
      return ({ onNavigate }) => onNavigate('clients');
    case 'whatsapp':
      return ({ onConnectWa }) => onConnectWa?.();
    case 'calendar':
      return ({ onConnectGoogle }) => onConnectGoogle?.();
    case 'documents':
      return ({ onNavigate }) => onNavigate('documents');
  }
}
